import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Lightbox from '../Lightbox';
import { formatDate } from '../../utils/format';
import TranslationNotice from '../TranslationNotice';
import { kindLabel, storyDate, clubName, loadStories, chronological } from './common';
import { t, tt, route, find, syncLocale, contentLang } from '../../i18n';

/**
 * /dark-pages/:slug — één verhaal: het verhaal zelf, tijdlijn, getuigenissen van het forum, krantenknipsels en bronnen.
 */
export default class DarkStoryPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.lightbox = null;
    this.all = [];
    syncLocale();
    this.load(m.route.param('slug'));

    loadStories().then((stories) => {
      this.all = chronological(stories);
      m.redraw();
    });
    if (app.store.all('lc-clubs').length < 20) find('lc-clubs').then(() => m.redraw());
  }

  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    const slug = m.route.param('slug');
    if (this.story && slug !== this.story.slug()) {
      this.lightbox = null;
      this.load(slug);
      window.scrollTo(0, 0);
    }
  }

  load(slug) {
    this.story = app.store.all('lc-dark-stories').find((s) => s.slug() === slug && s.bodyHtml() !== undefined) || null;
    if (this.story) this.ready();

    find('lc-dark-stories', slug).then((story) => {
      this.story = story;
      this.ready();
      m.redraw();
    });
  }

  ready() {
    app.history.push('lc.dark.story', this.story.title());
    app.setTitle(this.story.title());
    app.setTitleCount(0);
  }

  view() {
    const story = this.story;
    if (!story) return <div className="LcDark"><LoadingIndicator /></div>;

    return (
      <div className="LcDark LcDarkStory">
        {this.hero()}

        <div className="container LcDarkStory-body">
          <article className="LcDarkStory-main">
            {story.bodyHtml() ? (
              <div className="LcDarkStory-text" lang={contentLang(story)}>
                {m.trust(story.bodyHtml())}
              </div>
            ) : null}
            {this.timeline()}
            {this.quotes()}
            {this.images()}
          </article>
          <aside className="LcDarkStory-side">{this.sidebar()}</aside>
        </div>

        {this.pager()}

        {this.lightbox ? (
          <Lightbox
            items={this.lightbox.items}
            index={this.lightbox.index}
            base=""
            onclose={() => {
              this.lightbox = null;
              m.redraw();
            }}
          />
        ) : null}
      </div>
    );
  }

  hero() {
    const story = this.story;
    const clubs = story.clubSlugs() || [];

    return (
      <section className="LcDark-hero LcDarkStory-hero">
        <div className="container">
          <Link className="LcDark-back" href={route('lc.dark')}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> The Dark Pages
          </Link>
          <p className="LcDark-meta">
            {story.isPublished() ? null : <span className="LcDark-draft">{t('dark.draft_admin')}</span>}
            <span className={'LcDark-tag LcDark-tag--' + story.kind()}>{kindLabel(story.kind())}</span>
            <span>{storyDate(story)}</span>
            {story.place() ? <span>{story.place()}</span> : null}
          </p>
          <h1 className="LcDark-title LcDarkStory-title" lang={contentLang(story)}>
            {story.title()}
          </h1>
          {story.summary() ? (
            <p className="LcDark-lead" lang={contentLang(story)}>
              {story.summary()}
            </p>
          ) : null}
          <TranslationNotice model={story} />
          {clubs.length ? (
            <p className="LcDarkStory-clubs">
              {clubs.map((s) => (
                <Link href={route('lc.club', { slug: s })} className="LcDarkStory-club">
                  <i className="fas fa-compact-disc" aria-hidden="true" /> {clubName(s) || s}
                </Link>
              ))}
            </p>
          ) : null}
        </div>
      </section>
    );
  }

  timeline() {
    const items = this.story.timeline() || [];
    if (!items.length) return null;

    return (
      <section className="LcDarkStory-section">
        <h2>{t('dark.timeline')}</h2>
        <ol className="LcDark-timeline" lang={contentLang(this.story)}>
          {items.map((i) => (
            <li>
              <span className="LcDark-timelineDate">{i.date ? formatDate(i.date) : ''}</span>
              <p>{i.text}</p>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  /** Getuigenissen: letterlijke citaten uit het forum, anoniem, met een link naar het bericht. */
  quotes() {
    const items = this.story.quotes() || [];
    if (!items.length) return null;

    return (
      <section className="LcDarkStory-section">
        <h2>{t('dark.witnesses')}</h2>
        <p className="LcDark-note">{t(this.story.kind() === 'persoonlijk' ? 'dark.witnesses_note_personal' : 'dark.witnesses_note')}</p>
        <div className="LcDark-quotes">
          {items.map((q) => (
            <figure className="LcDark-quote">
              <blockquote lang="nl">{q.text}</blockquote>
              <figcaption>
                {q.context ? <span>{q.context}</span> : null}
                {q.date ? <span>{formatDate(q.date)}</span> : null}
                {q.discussion_id ? (
                  <a href={app.forum.attribute('baseUrl') + `/d/${q.discussion_id}/${q.number || 1}`}>
                    {t('dark.read_post')} <i className="fas fa-arrow-right" aria-hidden="true" />
                  </a>
                ) : null}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
    );
  }

  /** Krantenknipsels en ander beeldmateriaal. */
  images() {
    const items = this.story.images() || [];
    if (!items.length) return null;

    return (
      <section className="LcDarkStory-section">
        <h2>{t('dark.clippings')}</h2>
        <ul className="LcDark-clippings">
          {items.map((img, i) => (
            <li>
              <button onclick={() => (this.lightbox = { items, index: i })}>
                <img src={img.thumb || img.src} alt={img.caption || ''} loading="lazy" />
              </button>
              {img.caption ? <p>{img.caption}</p> : null}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  sidebar() {
    const story = this.story;
    const sources = story.sources() || [];
    const help = ['dood', 'drugs', 'persoonlijk'].includes(story.kind());

    return [
      sources.length ? (
        <section className="LcDark-panel">
          <h3>{t('dark.sources')}</h3>
          <ul className="LcDark-sources">
            {sources.map((s) => (
              <li>
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noopener nofollow ugc">
                    {s.title || s.url}
                  </a>
                ) : (
                  <span>{s.title}</span>
                )}
                {s.publisher || s.date ? <small>{[s.publisher, s.date ? formatDate(s.date) : null].filter(Boolean).join(' · ')}</small> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      <section className="LcDark-panel LcDark-panel--note">
        <p>{t('dark.warning')}</p>
        <p>{t('dark.correction')}</p>
      </section>,

      help ? (
        <section className="LcDark-panel LcDark-panel--help">
          <h3>{t('dark.help_title')}</h3>
          <p>
            {t('dark.help_drugs', {
              tel: <a href={'tel:' + tt('dark.help_drugs_tel')} />,
              site: <a href={tt('dark.help_drugs_url')} target="_blank" rel="noopener" />,
            })}
          </p>
          <p>
            {t('dark.help_talk', {
              tel: <a href={'tel:' + tt('dark.help_talk_tel')} />,
              tel2: <a href={'tel:' + tt('dark.help_talk_tel2')} />,
              site: <a href={tt('dark.help_talk_url')} target="_blank" rel="noopener" />,
            })}
          </p>
        </section>
      ) : null,
    ];
  }

  /** Vorig en volgend verhaal in de tijd. */
  pager() {
    const i = this.all.findIndex((s) => s.slug() === this.story.slug());
    if (i < 0) return null;
    const prev = this.all[i - 1];
    const next = this.all[i + 1];
    if (!prev && !next) return null;

    const link = (s, dir) =>
      s ? (
        <Link href={route('lc.dark.story', { slug: s.slug() })} className={'LcDark-pagerLink LcDark-pagerLink--' + dir}>
          <small>
            {t(dir === 'prev' ? 'dark.earlier' : 'dark.later')} · {storyDate(s)}
          </small>
          <strong>{s.title()}</strong>
        </Link>
      ) : (
        <span />
      );

    return (
      <nav className="container LcDark-pager" aria-label={tt('dark.pager_label')}>
        {link(prev, 'prev')}
        {link(next, 'next')}
      </nav>
    );
  }
}
